import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import realDataAccountingService from '../../services/realDataAccountingService';
import financialCalculationService from '../../services/financialCalculationService';
import {
  setCashFlowStatement,
  setReportLoading,
  setReportError,
  setPrintData
} from '../../redux/accounting';

const CashFlowStatement = () => {
  const dispatch = useDispatch();
  const { cashFlowStatement, reportsLoading, reportsError } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  // Get currency from school details
  const currency = schooldetails && schooldetails.length > 0 && schooldetails[0].currency 
    ? schooldetails[0].currency 
    : (schooldetails && schooldetails.length > 0 && schooldetails[0].country === 'LR' ? 'USD' : 'GHS');
  
  const getCurrencySymbol = () => {
    return currency === 'GHS' ? '₵' : currency === 'USD' ? '$' : currency;
  };
  
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || []
  };

  const loadCashFlowStatement = useCallback(async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    dispatch(setReportLoading({ reportType: 'cashFlow', loading: true }));
    
    try {
      // Get all financial data
      const endDateObj = new Date(endDate);
      const startDateObj = new Date(startDate);
      
      const financialData = await realDataAccountingService.getRealDashboardData(
        currentSchool?.schoolID,
        reduxData
      );

      // Generate cash flow statement using financial calculation service
      const cashFlowData = financialCalculationService.generateCashFlowStatement(financialData);
      
      dispatch(setCashFlowStatement(cashFlowData));
    } catch (err) {
      setError('Failed to load cash flow statement');
      dispatch(setReportError({ reportType: 'cashFlow', error: err.message }));
      console.error('Cash flow statement error:', err);
    } finally {
      setLoading(false);
      dispatch(setReportLoading({ reportType: 'cashFlow', loading: false }));
    }
  }, [currentSchool?.schoolID, startDate, endDate, reduxData, dispatch]);

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadCashFlowStatement();
    }
  }, [currentSchool, startDate, endDate, loadCashFlowStatement]);

  const handleRefresh = () => {
    loadCashFlowStatement();
  };

  const handlePrint = () => {
    const printData = {
      title: 'Cash Flow Statement',
      schoolName: currentSchool?.schoolname || 'School',
      period: `${startDate} to ${endDate}`,
      data: cashFlowStatement
    };
    dispatch(setPrintData({ cashFlow: printData }));
    window.print();
  };

  const handleExport = () => {
    // Export functionality would go here
    console.log('Exporting cash flow statement...');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!cashFlowStatement) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No Cash Flow Statement data available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cash Flow Statement
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Date Range */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Report Period</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cash Flow Statement */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6">
            {currentSchool?.schoolname || 'School'} Cash Flow Statement
          </Typography>
          <Typography variant="body2">
            For the period {startDate} to {endDate}
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount ({getCurrencySymbol()})</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Operating Activities */}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>
                  CASH FLOWS FROM OPERATING ACTIVITIES
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>School Fees Collected</TableCell>
                <TableCell align="right">
                  {cashFlowStatement.operating?.schoolFees?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Canteen Revenue</TableCell>
                <TableCell align="right">
                  {cashFlowStatement.operating?.canteenRevenue?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Other Operating Revenue</TableCell>
                <TableCell align="right">
                  {cashFlowStatement.operating?.otherRevenue?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Salaries Paid</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  -{cashFlowStatement.operating?.salaries?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Operating Expenses</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  -{cashFlowStatement.operating?.operatingExpenses?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Net Cash from Operating Activities</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {cashFlowStatement.operating?.netCash?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>

              {/* Investing Activities */}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8' }}>
                  CASH FLOWS FROM INVESTING ACTIVITIES
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Asset Purchases</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  -{cashFlowStatement.investing?.assetPurchases?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Asset Sales</TableCell>
                <TableCell align="right">
                  {cashFlowStatement.investing?.assetSales?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Net Cash from Investing Activities</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {cashFlowStatement.investing?.netCash?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>

              {/* Financing Activities */}
              <TableRow>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0' }}>
                  CASH FLOWS FROM FINANCING ACTIVITIES
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Capital Contributions</TableCell>
                <TableCell align="right">
                  {cashFlowStatement.financing?.capitalContributions?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Loan Proceeds</TableCell>
                <TableCell align="right">
                  {cashFlowStatement.financing?.loanProceeds?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Loan Repayments</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  -{cashFlowStatement.financing?.loanRepayments?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
              
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Net Cash from Financing Activities</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {cashFlowStatement.financing?.netCash?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>

              {/* Net Cash Change */}
              <TableRow sx={{ backgroundColor: '#e1f5fe', borderTop: '2px solid #1976d2' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Net Increase (Decrease) in Cash
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {cashFlowStatement.netCashChange?.toLocaleString() || '0.00'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Cash Flow Summary</Typography>
          <Typography variant="body2" color="text.secondary">
            This statement shows how cash and cash equivalents have changed during the period. 
            Positive values indicate cash inflows, while negative values indicate cash outflows.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashFlowStatement;
